/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/control-has-associated-label */

import React, { memo } from 'react'
import classNames from 'classnames'
import Color from 'color'
import { Avatar } from 'transfix-ui/components/Avatar'
import { Box, Link, ListItem, ListItemAvatar, Typography } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { getTimeAgoShort } from 'utils/formatDatetime'
import { TSingleNotification } from '../../types'

const infoLightHover = Color(brandColors.info).mix(Color(brandColors.infoLight), 0.98).hex()

const useNotificationListItemStyles = makeStyles(theme => ({
  root: {
    padding: `${theme.spacing(1.5)} !important`,
    borderTop: `1px solid ${brandColors.coolGray4}`,
    alignItems: 'flex-start',

    '&:first-child': {
      borderTop: 'none',
    },

    '&:hover': {
      backgroundColor: '#fbfbfb',
    },
  },
  rootUnread: {
    backgroundColor: brandColors.infoLight,

    '&:hover': {
      backgroundColor: infoLightHover,
    },
  },
  listItemAvatar: {
    maxWidth: 32,
    minWidth: 32,
    marginRight: theme.spacing(1.5),
  },
  listItemContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  metaData: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: '#697684',
  },
  markAsReadButton: {
    height: '10px',
    width: '10px',
    display: 'inline-block',
    backgroundColor: brandColors.info,
    borderRadius: '50%',
    cursor: 'pointer',
    marginLeft: '5px',
    marginTop: '1px',

    '&:hover': {
      backgroundColor: brandColors.infoDark,
    },
  },
  markAsReadButtonIsRead: {
    display: 'none',
  },
}))

export interface INotificationListItemProps {
  notification: TSingleNotification
  onMarkAsRead: (id: string) => void
}

/**
 * Single notification item
 */
const NotificationListItem = memo<INotificationListItemProps>(
  ({ notification, onMarkAsRead: onMarkAsReadProp }) => {
    const classes = useNotificationListItemStyles()
    const { body, createdAt, createdBy, id, readAt, title, url } = notification || {}
    const { fullName = '' } = createdBy || {}

    const markAsReadHandler = () => onMarkAsReadProp(notification.id)

    const className = classNames(classes.root, {
      [classes.rootUnread]: !readAt,
    })

    return (
      <ListItem className={className} data-test='notification-list-item'>
        {/**
         * Avatar
         */}
        <ListItemAvatar className={classes.listItemAvatar}>
          <Avatar id={id} hideTooltip size='small' name={fullName ?? ''} />
        </ListItemAvatar>
        <Box className={classes.listItemContent}>
          <Box display='flex' flexDirection='row'>
            <Box flexGrow={1}>
              <Typography variant='button' color='textPrimary' component='div' gutterBottom>
                <Link target='_blank' href={url}>
                  {title}
                </Link>
              </Typography>
            </Box>
            <Box className={classes.metaData}>
              <Box>
                <Typography variant='caption' component='div' color='textSecondary'>
                  {getTimeAgoShort(createdAt)}
                </Typography>
              </Box>
              <Box>
                <span
                  role='button'
                  className={classNames(classes.markAsReadButton, {
                    [classes.markAsReadButtonIsRead]: !!readAt,
                  })}
                  onClick={markAsReadHandler}
                />
              </Box>
            </Box>
          </Box>
          {/**
           * Message Body
           */}
          <Box>
            <Typography variant='body2' component='div' gutterBottom>
              {body}
            </Typography>
            {createdBy?.fullName && (
              <Typography variant='caption' color='textSecondary'>
                From: {createdBy.fullName}
              </Typography>
            )}
          </Box>
        </Box>
      </ListItem>
    )
  }
)

export default NotificationListItem
